/**
 * Centralized, typed analytics dispatcher for MTB BTP.
 *
 * Contract:
 *  - No PII leaves the client. Names, phone numbers, emails, free-form text,
 *    documents, Drive IDs, storage paths, or any user-authored content are
 *    forbidden in event properties. Only opaque booleans, numbers, sizes,
 *    hashed / bucketed identifiers, and enum tags are allowed.
 *  - PostHog is optional. If posthog-js is unavailable or hasn't been
 *    initialized (dev / preview / ad blocker), tracking is a no-op instead
 *    of throwing.
 *  - Sentry breadcrumbs mirror events for correlation, but only with the same
 *    sanitized payload — the PII filter applies to both transports.
 *
 * This module is the ONLY sanctioned way to send analytics from the app.
 * Direct `posthog.capture(...)` calls in feature code are forbidden.
 */

import { posthog } from '@/lib/posthog';
import { getConsent } from '@/lib/consent';

import { Sentry } from '@/lib/sentry';

/** Enumerated event names. Add here first, then call from the UI. */
export type AnalyticsEventName =
  | 'quote_form_submitted'
  | 'click_qualification'
  | 'click_simulateur'
  | 'click_visite'
  | 'click_presentation'
  | 'click_espace_client'
  | 'click_pilotage_admin'
  | 'contact_form_submitted'
  | 'contact_form_error'
  | 'careers_form_submitted'
  | 'careers_form_error'
  | 'actualites_filter_applied'
  | 'site_update_opened'
  | 'quote_form_error'
  | 'whatsapp_click'
  | 'phone_click'
  | 'drive_backup_started'
  | 'drive_backup_succeeded'
  | 'drive_backup_failed'
  | 'auth_login_error'
  | 'crm_prospect_created'
  | 'crm_status_changed'
  | 'crm_converted_to_quote'
  | 'crm_converted_to_chantier'
  | 'chantier_attachment_uploaded'
  | 'mobile_performance_sample';

/** Allowed property types. */
type Primitive = string | number | boolean | null;
export type AnalyticsProps = Record<string, Primitive | undefined>;

/**
 * Keys that must never appear in a payload. This is deliberately broad — the
 * intent is defense-in-depth against accidental leaks, not the source of truth
 * for what is allowed.
 */
const FORBIDDEN_KEY = /(^|_)(name|full_name|first_name|last_name|email|mail|phone|tel|address|street|city|postal|note|notes|message|description|summary|content|body|title|drive_?id|drive_?file_?id|drive_?url|storage_?path|file_?name|filename|token|password|secret|api_?key|bearer)(_|$)/i;

/** Substrings that suggest PII in a value even if the key is opaque. */
const SUSPICIOUS_VALUE_PATTERNS: RegExp[] = [
  /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i,          // email
  /(\+?\d[\d\s().-]{7,})/,                            // phone-like
];

function isSafeString(v: string): boolean {
  if (v.length > 128) return false;
  if (SUSPICIOUS_VALUE_PATTERNS.some((re) => re.test(v))) return false;
  return true;
}

/** Sanitize a payload. Returns a new object; drops forbidden keys and unsafe values. */
export function sanitize(props: AnalyticsProps | undefined): Record<string, Primitive> {
  const out: Record<string, Primitive> = {};
  if (!props) return out;
  for (const [k, raw] of Object.entries(props)) {
    if (raw === undefined) continue;
    if (FORBIDDEN_KEY.test(k)) continue;
    if (typeof raw === 'string') {
      if (!isSafeString(raw)) continue;
      out[k] = raw;
    } else if (typeof raw === 'number' && Number.isFinite(raw)) {
      out[k] = raw;
    } else if (typeof raw === 'boolean' || raw === null) {
      out[k] = raw;
    }
  }
  return out;
}

interface PosthogLike {
  capture?: (name: string, props?: Record<string, unknown>) => void;
  __loaded?: boolean;
}

function posthogAvailable(): PosthogLike | null {
  try {
    // Aucun événement n'est envoyé sans consentement cookie explicite.
    if (getConsent() !== 'granted') return null;
    const ph = posthog as unknown as PosthogLike;
    if (!ph || typeof ph.capture !== 'function') return null;
    // posthog-js sets __loaded once init() succeeds; we don't require it
    // strictly, but skip if the object is a stub.
    return ph;
  } catch {
    return null;
  }
}


/** Fire-and-forget event dispatch. Never throws. */
export function track(name: AnalyticsEventName, props?: AnalyticsProps): void {
  const clean = sanitize(props);
  try {
    const ph = posthogAvailable();
    if (ph?.capture) ph.capture(name, clean);
  } catch (err) {
    // Swallow — analytics must never break the UI.
    if (import.meta.env.DEV) console.warn('[analytics] posthog failed', err);
  }
  try {
    Sentry.addBreadcrumb?.({
      category: 'analytics',
      level: 'info',
      message: name,
      data: clean,
    });
  } catch {
    /* ignore */
  }
}

/**
 * Report a technical exception with sanitized context.
 * Only non-sensitive tags (code, HTTP status, route slug) should be passed.
 */
export function reportError(err: unknown, context?: AnalyticsProps): void {
  const clean = sanitize(context);
  try {
    Sentry.captureException?.(err, { extra: clean });
  } catch {
    /* ignore */
  }
}

/** Test-only export. Not part of the public surface. */
export const __internal = { FORBIDDEN_KEY, SUSPICIOUS_VALUE_PATTERNS, isSafeString };
